import { useState, useEffect } from "react";

// Point this at your monorepo backend (router mounted at /quizclash)
const BACKEND = "https://learntok-backend-2026-24c204fe508e.herokuapp.com/quizclash";

const DIFFICULTY = [
  { id: "easy", label: "Easy", sub: "warm-ups" },
  { id: "medium", label: "Medium", sub: "a fair fight" },
  { id: "hard", label: "Hard", sub: "for the confident" },
  { id: "expert", label: "Expert", sub: "humble the room" },
];
const VIBE = [
  { id: "snappy", label: "Snappy facts", sub: "quick recall" },
  { id: "tricky", label: "Tricky", sub: "watch the wording" },
  { id: "scenario", label: "Scenario", sub: "apply it" },
  { id: "mixed", label: "Mixed bag", sub: "a bit of all" },
];
const GOAL = [
  { id: "learn", label: "Learn it", sub: "" },
  { id: "test", label: "Test myself", sub: "" },
  { id: "class", label: "Class test", sub: "for students" },
];

const LOADING = ["Writing fresh questions…", "Drafting tricky options…", "Spreading across 10 angles…", "Polishing the deck…"];

async function api(path, opts) {
  const r = await fetch(BACKEND + path, {
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "request failed");
  return r.json();
}

const C = {
  bg: "#FAF4E9", ink: "#181826", coral: "#FF5436", teal: "#0E8B7E",
  gold: "#F2B705", paper: "#FFFFFF", line: "#E6DCC8", soft: "#7A7468",
};

export default function App() {
  const [screen, setScreen] = useState("home"); // home customize generating editor sharing playing results
  const [topic, setTopic] = useState("");
  const [settings, setSettings] = useState({ difficulty: "medium", vibe: "mixed", goal: "test" });
  const [cards, setCards] = useState([]);
  const [code, setCode] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [isExisting, setIsExisting] = useState(false); // editing a published quiz?

  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [answered, setAnswered] = useState(0);
  const [isOwnerPreview, setIsOwnerPreview] = useState(false);

  const [name, setName] = useState("");
  const [board, setBoard] = useState([]);
  const [posted, setPosted] = useState(false);

  const [joinInput, setJoinInput] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loadMsg, setLoadMsg] = useState(0);

  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,900&family=DM+Sans:wght@400;500;700&display=swap";
    document.head.appendChild(l);
  }, []);
  useEffect(() => {
    if (screen !== "generating") return;
    const t = setInterval(() => setLoadMsg(m => (m + 1) % LOADING.length), 1600);
    return () => clearInterval(t);
  }, [screen]);

  function resetPlay() { setIdx(0); setPicked(null); setCorrect(0); setAnswered(0); setName(""); setBoard([]); setPosted(false); }

  async function generate() {
    setScreen("generating"); setErr("");
    try {
      const { cards } = await api("/generate", { method: "POST", body: JSON.stringify({ topic, settings }) });
      setCards(cards); setIsExisting(false); setCode(""); setScreen("editor");
    } catch (e) { setErr(e.message); setScreen("customize"); }
  }

  async function publish() {
    setBusy(true); setErr("");
    try {
      if (isExisting && code) {
        await api(`/quiz/${code}`, { method: "PATCH", body: JSON.stringify({ topic, settings, cards, ownerName }) });
      } else {
        const { code: newCode } = await api("/quiz", { method: "POST", body: JSON.stringify({ topic, settings, cards, ownerName }) });
        setCode(newCode); setIsExisting(true);
      }
      await loadBoard(code || undefined);
      setScreen("sharing");
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  async function joinByCode(raw) {
    const c = raw.trim().toUpperCase();
    if (!c) return;
    setBusy(true); setErr("");
    try {
      const { quiz } = await api(`/quiz/${c}`);
      setTopic(quiz.topic); setSettings(quiz.settings || settings); setCards(quiz.cards);
      setCode(quiz.code); setIsExisting(true); resetPlay(); setIsOwnerPreview(false);
      setScreen("playing");
    } catch (e) { setErr("No challenge found for that code."); }
    setBusy(false);
  }

  async function loadBoard(c) {
    try { const { leaderboard } = await api(`/quiz/${c || code}/leaderboard`); setBoard(leaderboard || []); }
    catch (e) {}
  }

  function pick(i) {
    if (picked !== null) return;
    setPicked(i); setAnswered(a => a + 1);
    if (i === cards[idx].answer) setCorrect(c => c + 1);
  }
  async function next() {
    if (idx + 1 >= cards.length) {
      if (isOwnerPreview) { setScreen("sharing"); await loadBoard(); }
      else { setScreen("results"); await loadBoard(); }
    } else { setIdx(idx + 1); setPicked(null); }
  }
  async function postScore() {
    setBusy(true);
    try {
      const { leaderboard } = await api(`/quiz/${code}/score`, { method: "POST", body: JSON.stringify({ name: name.trim() || "Anonymous", correct, answered, total: cards.length }) });
      setBoard(leaderboard || []); setPosted(true);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  function shareText() { return `Take my "${topic}" quiz on QuizClash and beat my score. Open the app and enter code: ${code}`; }
  async function copyShare() {
    const txt = shareText();
    try { await navigator.clipboard.writeText(txt); }
    catch (e) { const ta = document.createElement("textarea"); ta.value = txt; document.body.appendChild(ta); ta.select(); try { document.execCommand("copy"); } catch (e2) {} document.body.removeChild(ta); }
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  }

  // ---- editor card ops ----
  const setCard = (i, patch) => setCards(cs => cs.map((c, j) => j === i ? { ...c, ...patch } : c));
  const setOpt = (i, j, v) => setCards(cs => cs.map((c, k) => k === i ? { ...c, options: c.options.map((o, m) => m === j ? v : o) } : c));
  const delCard = (i) => setCards(cs => cs.filter((_, j) => j !== i));
  const move = (i, d) => setCards(cs => { const a = [...cs]; const n = i + d; if (n < 0 || n >= a.length) return a; [a[i], a[n]] = [a[n], a[i]]; return a; });
  const addCard = () => setCards(cs => [...cs, { q: "", options: ["", "", "", ""], answer: 0, explain: "" }]);

  const css = `
    *{box-sizing:border-box}
    .qc{font-family:'DM Sans',system-ui,sans-serif;color:${C.ink}}
    .disp{font-family:'Fraunces',serif}
    @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
    @keyframes pop{0%{transform:scale(.92)}60%{transform:scale(1.03)}100%{transform:scale(1)}}
    @keyframes shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-6px)}75%{transform:translateX(6px)}}
    @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
    .fade{animation:fadeUp .5s cubic-bezier(.2,.8,.2,1) both}
    .opt{transition:transform .12s,background .15s,border-color .15s;cursor:pointer}
    .opt:hover{transform:translateY(-2px)}
    .btn{transition:transform .12s,box-shadow .15s,opacity .15s;cursor:pointer}
    .btn:hover{transform:translateY(-2px)}.btn:active{transform:translateY(0)}
    .search:focus{outline:none;border-color:${C.coral};box-shadow:0 8px 30px rgba(255,84,54,.18)}
    input,textarea{font-family:'DM Sans',sans-serif}
    input::placeholder,textarea::placeholder{color:#b6ad99}
    .ed-in{width:100%;border:2px solid ${C.line};border-radius:10px;padding:10px 12px;font-size:15px;background:#fff}
    .ed-in:focus{outline:none;border-color:${C.teal}}
  `;
  const shell = (children, max = 760) => (
    <div className="qc" style={{ minHeight: "100vh", background: C.bg, padding: "32px 18px 80px" }}>
      <style>{css}</style>
      <div style={{ maxWidth: max, margin: "0 auto" }}>{children}</div>
    </div>
  );
  const errBar = err ? <div style={{ background: "#FFF1ED", color: C.coral, padding: "10px 14px", borderRadius: 10, marginBottom: 16, fontSize: 14 }}>{err}</div> : null;

  // ---------------- HOME ----------------
  if (screen === "home") {
    return shell(
      <div>
        <div className="fade" style={{ textAlign: "center", marginTop: "8vh" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: C.ink, color: C.bg, padding: "6px 14px", borderRadius: 999, fontSize: 13, fontWeight: 700, letterSpacing: ".5px" }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: C.gold }} /> QUIZCLASH
          </div>
          <h1 className="disp" style={{ fontSize: "clamp(38px,7vw,68px)", fontWeight: 900, lineHeight: 1.02, margin: "22px 0 10px", letterSpacing: "-1.5px" }}>
            What should we<br /><span style={{ color: C.coral }}>quiz you</span> on?
          </h1>
          <p style={{ color: C.soft, fontSize: 17, margin: "0 0 30px" }}>Type a topic. Get 60 cards. Edit them, then dare your group chat or your class.</p>
          {errBar}
          <div style={{ position: "relative", maxWidth: 560, margin: "0 auto" }}>
            <input className="search" value={topic} onChange={e => setTopic(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && topic.trim()) setScreen("customize"); }}
              placeholder="e.g. Roman emperors, photosynthesis, F1 history…"
              style={{ width: "100%", fontSize: 18, padding: "20px 130px 20px 24px", borderRadius: 18, border: `2px solid ${C.line}`, background: C.paper }} />
            <button className="btn" disabled={!topic.trim()} onClick={() => setScreen("customize")}
              style={{ position: "absolute", right: 8, top: 8, bottom: 8, padding: "0 22px", border: "none", borderRadius: 13, background: topic.trim() ? C.coral : "#d8cdb6", color: "#fff", fontWeight: 700, fontSize: 16 }}>Start →</button>
          </div>
        </div>

        <div className="fade" style={{ maxWidth: 560, margin: "44px auto 0", background: C.paper, border: `2px solid ${C.line}`, borderRadius: 18, padding: 22 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚔️ Got a code?</div>
          <p style={{ color: C.soft, fontSize: 14, margin: "0 0 12px" }}>Enter a 5-letter code to take someone's quiz and climb their ranking.</p>
          <div style={{ display: "flex", gap: 10 }}>
            <input value={joinInput} onChange={e => { setJoinInput(e.target.value.toUpperCase()); setErr(""); }}
              onKeyDown={e => { if (e.key === "Enter") joinByCode(joinInput); }} placeholder="ABCDE" maxLength={5}
              style={{ flex: 1, fontSize: 18, letterSpacing: "4px", fontWeight: 700, padding: "14px 16px", borderRadius: 12, border: `2px solid ${C.line}`, textTransform: "uppercase" }} />
            <button className="btn" disabled={busy || joinInput.length < 4} onClick={() => joinByCode(joinInput)}
              style={{ padding: "0 22px", border: "none", borderRadius: 12, background: C.teal, color: "#fff", fontWeight: 700, fontSize: 16 }}>{busy ? "…" : "Join"}</button>
          </div>
        </div>
        <Footer onCreate={(t) => { setTopic(t); setScreen("customize"); }} />
      </div>
    );
  }

  // ---------------- CUSTOMIZE ----------------
  if (screen === "customize") {
    const Group = ({ title, list, k }) => (
      <div style={{ marginBottom: 26 }}>
        <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 15 }}>{title}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10 }}>
          {list.map(o => { const on = settings[k] === o.id; return (
            <div key={o.id} className="opt" onClick={() => setSettings(s => ({ ...s, [k]: o.id }))}
              style={{ padding: "14px 16px", borderRadius: 14, border: `2px solid ${on ? C.coral : C.line}`, background: on ? "#FFF1ED" : C.paper, animation: on ? "pop .25s" : "none" }}>
              <div style={{ fontWeight: 700 }}>{o.label}</div>{o.sub && <div style={{ fontSize: 12.5, color: C.soft, marginTop: 2 }}>{o.sub}</div>}
            </div>); })}
        </div>
      </div>
    );
    return shell(
      <div className="fade">
        <button className="btn" onClick={() => setScreen("home")} style={{ background: "none", border: "none", color: C.soft, fontSize: 14, padding: 0, marginBottom: 18, fontWeight: 600 }}>← back</button>
        <div style={{ color: C.soft, fontSize: 14, fontWeight: 600 }}>YOUR QUIZ</div>
        <h2 className="disp" style={{ fontSize: "clamp(28px,5vw,42px)", fontWeight: 900, margin: "4px 0 26px", letterSpacing: "-1px", lineHeight: 1.05 }}>{topic}</h2>
        {errBar}
        <Group title="1. How hard?" list={DIFFICULTY} k="difficulty" />
        <Group title="2. Question style?" list={VIBE} k="vibe" />
        <Group title="3. What's it for?" list={GOAL} k="goal" />
        <button className="btn" onClick={generate} style={{ width: "100%", padding: "18px", border: "none", borderRadius: 16, background: C.ink, color: C.bg, fontWeight: 800, fontSize: 18, boxShadow: "0 10px 28px rgba(24,24,38,.28)" }}>Generate 60 cards ✨</button>
      </div>
    );
  }

  // ---------------- GENERATING ----------------
  if (screen === "generating") {
    return shell(
      <div className="fade" style={{ textAlign: "center", marginTop: "16vh" }}>
        <div style={{ fontSize: 54, animation: "float 2s ease-in-out infinite" }}>🧠</div>
        <h2 className="disp" style={{ fontSize: 30, fontWeight: 900, margin: "16px 0 6px" }}>Building your deck…</h2>
        <p style={{ color: C.soft, margin: 0 }}>{LOADING[loadMsg]} <b>{topic}</b></p>
      </div>, 760
    );
  }

  // ---------------- EDITOR ----------------
  if (screen === "editor") {
    return shell(
      <div className="fade">
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: C.soft, fontSize: 14, fontWeight: 600 }}>{isExisting ? "EDIT QUIZ" : "REVIEW & EDIT"} · {cards.length} cards</div>
          <h2 className="disp" style={{ fontSize: "clamp(24px,4vw,34px)", fontWeight: 900, margin: "4px 0 6px", letterSpacing: "-1px" }}>{topic}</h2>
          <p style={{ color: C.soft, fontSize: 14.5, margin: 0 }}>Tweak any question, fix the answer, delete or add cards. Great for setting a class test before you share the code.</p>
        </div>
        {errBar}
        <input value={ownerName} onChange={e => setOwnerName(e.target.value)} placeholder="Your name (shown as quiz author, optional)" className="ed-in" style={{ marginBottom: 18 }} />

        <div style={{ display: "grid", gap: 14 }}>
          {cards.map((c, i) => (
            <div key={i} style={{ background: C.paper, border: `2px solid ${C.line}`, borderRadius: 16, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <span className="disp" style={{ fontWeight: 900, color: C.soft }}>#{i + 1}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <Mini onClick={() => move(i, -1)} disabled={i === 0}>↑</Mini>
                  <Mini onClick={() => move(i, 1)} disabled={i === cards.length - 1}>↓</Mini>
                  <Mini onClick={() => delCard(i)} danger>✕ delete</Mini>
                </div>
              </div>
              <textarea value={c.q} onChange={e => setCard(i, { q: e.target.value })} placeholder="Question…" rows={2} className="ed-in" style={{ resize: "vertical", marginBottom: 10, fontWeight: 600 }} />
              <div style={{ display: "grid", gap: 8 }}>
                {c.options.map((o, j) => {
                  const on = c.answer === j;
                  return (
                    <div key={j} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button className="btn" onClick={() => setCard(i, { answer: j })} title="Mark correct"
                        style={{ width: 30, height: 30, flexShrink: 0, borderRadius: 8, border: `2px solid ${on ? C.teal : C.line}`, background: on ? C.teal : "#fff", color: on ? "#fff" : C.soft, fontWeight: 800 }}>
                        {on ? "✓" : String.fromCharCode(65 + j)}
                      </button>
                      <input value={o} onChange={e => setOpt(i, j, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + j)}`} className="ed-in" style={{ borderColor: on ? C.teal : C.line }} />
                    </div>
                  );
                })}
              </div>
              <input value={c.explain} onChange={e => setCard(i, { explain: e.target.value })} placeholder="Explanation (optional)" className="ed-in" style={{ marginTop: 8, fontSize: 13.5, color: C.soft }} />
            </div>
          ))}
        </div>

        <button className="btn" onClick={addCard} style={{ width: "100%", marginTop: 14, padding: "14px", border: `2px dashed ${C.line}`, borderRadius: 14, background: "transparent", fontWeight: 700, color: C.soft }}>+ Add a card</button>

        <div style={{ position: "sticky", bottom: 14, marginTop: 18, display: "flex", gap: 10 }}>
          <button className="btn" disabled={busy} onClick={publish}
            style={{ flex: 1, padding: "17px", border: "none", borderRadius: 14, background: C.coral, color: "#fff", fontWeight: 800, fontSize: 17, boxShadow: "0 10px 26px rgba(255,84,54,.4)" }}>
            {busy ? "Publishing…" : isExisting ? "Save changes →" : "Publish & get share code →"}
          </button>
        </div>
      </div>
    );
  }

  // ---------------- SHARING (owner dashboard) ----------------
  if (screen === "sharing") {
    return shell(
      <div className="fade">
        <div style={{ textAlign: "center", marginTop: "2vh" }}>
          <div style={{ color: C.soft, fontWeight: 700, fontSize: 14 }}>{topic.toUpperCase()} · {cards.length} CARDS</div>
          <h2 className="disp" style={{ fontSize: "clamp(26px,5vw,40px)", fontWeight: 900, margin: "4px 0 0", letterSpacing: "-1px" }}>Your quiz is live 🎉</h2>
        </div>

        <div style={{ marginTop: 26, borderRadius: 22, padding: 26, background: `linear-gradient(135deg,${C.ink},#2a2a44)`, color: C.bg, textAlign: "center", boxShadow: "0 14px 40px rgba(24,24,38,.3)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".5px", opacity: .7 }}>SHARE THIS CODE</div>
          <div style={{ fontSize: 14, opacity: .8, margin: "8px 0 14px" }}>Send it to your group chat or class. Everyone plays your exact cards and lands on the ranking.</div>
          <div className="disp" style={{ fontSize: 52, fontWeight: 900, letterSpacing: "10px", color: C.gold, margin: "0 0 18px" }}>{code}</div>
          <button className="btn" onClick={copyShare} style={{ width: "100%", padding: "16px", border: "none", borderRadius: 14, background: C.coral, color: "#fff", fontWeight: 800, fontSize: 17, boxShadow: "0 8px 22px rgba(255,84,54,.4)" }}>
            {copied ? "Copied — paste it anywhere ✓" : "📋 Copy challenge link"}
          </button>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={() => { resetPlay(); setIsOwnerPreview(true); setScreen("playing"); }}
            style={{ flex: 1, padding: "14px", border: `2px solid ${C.line}`, borderRadius: 14, background: C.paper, fontWeight: 700 }}>▶ Take it yourself</button>
          <button className="btn" onClick={() => { setIsExisting(true); setScreen("editor"); }}
            style={{ flex: 1, padding: "14px", border: `2px solid ${C.line}`, borderRadius: 14, background: C.paper, fontWeight: 700 }}>✎ Edit questions</button>
        </div>

        <Leaderboard board={board} onRefresh={() => loadBoard()} />
        <Footer onCreate={(t) => { setTopic(t); resetPlay(); setIsExisting(false); setScreen("customize"); }} />
      </div>
    );
  }

  // ---------------- PLAYING ----------------
  if (screen === "playing") {
    const card = cards[idx];
    const pct = Math.round((idx / cards.length) * 100);
    return shell(
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.soft }}>{topic}{isOwnerPreview ? " · preview" : ""}</div>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <span style={{ fontWeight: 800, color: C.teal }}>★ {correct}</span>
            <button className="btn" onClick={next} style={{ background: "none", border: `1.5px solid ${C.line}`, borderRadius: 999, padding: "5px 14px", fontSize: 13, fontWeight: 700, color: C.soft }}>End & score</button>
          </div>
        </div>
        <div style={{ height: 8, background: C.line, borderRadius: 999, overflow: "hidden", marginBottom: 22 }}>
          <div style={{ width: pct + "%", height: "100%", background: C.coral, transition: "width .3s" }} />
        </div>
        <div key={idx} className="fade" style={{ background: C.paper, border: `2px solid ${C.line}`, borderRadius: 22, padding: "26px 24px", boxShadow: "0 12px 36px rgba(24,24,38,.08)" }}>
          <div style={{ color: C.soft, fontWeight: 700, fontSize: 13, marginBottom: 10 }}>CARD {idx + 1} / {cards.length}</div>
          <h2 className="disp" style={{ fontSize: "clamp(22px,3.6vw,30px)", fontWeight: 600, lineHeight: 1.25, margin: "0 0 22px" }}>{card.q}</h2>
          <div style={{ display: "grid", gap: 12 }}>
            {card.options.map((opt, i) => {
              const isAns = i === card.answer, isPicked = picked === i;
              let bg = C.paper, border = C.line;
              if (picked !== null) { if (isAns) { bg = "#E6F6F1"; border = C.teal; } else if (isPicked) { bg = "#FFECE8"; border = C.coral; } }
              return (
                <div key={i} className="opt" onClick={() => pick(i)}
                  style={{ padding: "16px 18px", borderRadius: 14, border: `2px solid ${border}`, background: bg, display: "flex", alignItems: "center", gap: 12, animation: picked !== null && isPicked && !isAns ? "shake .35s" : "none" }}>
                  <span style={{ width: 28, height: 28, borderRadius: 8, background: picked !== null && isAns ? C.teal : (isPicked ? C.coral : C.line), color: picked !== null && (isAns || isPicked) ? "#fff" : C.soft, display: "grid", placeItems: "center", fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                    {picked !== null && isAns ? "✓" : (picked !== null && isPicked ? "✕" : String.fromCharCode(65 + i))}
                  </span>
                  <span style={{ fontWeight: 500 }}>{opt}</span>
                </div>
              );
            })}
          </div>
          {picked !== null && card.explain && (
            <div className="fade" style={{ marginTop: 18, padding: "12px 16px", background: C.bg, borderRadius: 12, fontSize: 14.5, color: C.soft }}>
              <b style={{ color: C.ink }}>{picked === card.answer ? "Correct. " : "Nope. "}</b>{card.explain}
            </div>
          )}
        </div>
        {picked !== null && (
          <button className="btn fade" onClick={next} style={{ width: "100%", marginTop: 16, padding: "16px", border: "none", borderRadius: 14, background: C.ink, color: C.bg, fontWeight: 800, fontSize: 17 }}>
            {idx + 1 >= cards.length ? "See score →" : "Next card →"}
          </button>
        )}
      </div>
    );
  }

  // ---------------- RESULTS (player) ----------------
  if (screen === "results") {
    const pctScore = answered ? Math.round((correct / answered) * 100) : 0;
    return shell(
      <div className="fade">
        <div style={{ textAlign: "center", marginTop: "4vh" }}>
          <div style={{ color: C.soft, fontWeight: 700, fontSize: 14 }}>{topic.toUpperCase()}</div>
          <div className="disp" style={{ fontSize: "clamp(60px,16vw,120px)", fontWeight: 900, lineHeight: 1, color: C.coral, animation: "pop .5s" }}>
            {correct}<span style={{ color: C.line, fontSize: "0.5em" }}>/{answered}</span>
          </div>
          <p style={{ color: C.soft, fontSize: 17, margin: "4px 0 0" }}>
            {pctScore >= 80 ? "Elite. Now humble your friends." : pctScore >= 50 ? "Solid — but can your group beat it?" : "Rough — perfect bait to challenge friends."}
          </p>
        </div>
        {errBar}
        {!posted && (
          <div style={{ marginTop: 24, background: C.paper, border: `2px solid ${C.line}`, borderRadius: 18, padding: 20 }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Add yourself to the ranking</div>
            <div style={{ display: "flex", gap: 10 }}>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" onKeyDown={e => { if (e.key === "Enter") postScore(); }}
                style={{ flex: 1, fontSize: 16, padding: "13px 16px", borderRadius: 12, border: `2px solid ${C.line}` }} />
              <button className="btn" disabled={busy} onClick={postScore} style={{ padding: "0 22px", border: "none", borderRadius: 12, background: C.teal, color: "#fff", fontWeight: 700, fontSize: 16 }}>{busy ? "…" : "Post"}</button>
            </div>
          </div>
        )}
        <div style={{ marginTop: 22, borderRadius: 18, padding: 20, background: C.ink, color: C.bg, textAlign: "center" }}>
          <div style={{ fontSize: 13, opacity: .75, fontWeight: 700 }}>PASS IT ON</div>
          <div className="disp" style={{ fontSize: 40, fontWeight: 900, letterSpacing: "8px", color: C.gold, margin: "6px 0 14px" }}>{code}</div>
          <button className="btn" onClick={copyShare} style={{ width: "100%", padding: "14px", border: "none", borderRadius: 12, background: C.coral, color: "#fff", fontWeight: 800, fontSize: 16 }}>{copied ? "Copied ✓" : "📋 Copy challenge link"}</button>
        </div>
        <Leaderboard board={board} onRefresh={() => loadBoard()} />
        <button className="btn" onClick={() => { setScreen("home"); setTopic(""); setIsExisting(false); }} style={{ width: "100%", marginTop: 18, padding: "15px", border: `2px solid ${C.line}`, borderRadius: 14, background: C.paper, fontWeight: 700, fontSize: 16 }}>↺ Make your own quiz</button>
        <Footer onCreate={(t) => { setTopic(t); resetPlay(); setIsExisting(false); setScreen("customize"); }} />
      </div>
    );
  }

  return null;
}

function Mini({ children, onClick, disabled, danger }) {
  return <button className="btn" onClick={onClick} disabled={disabled}
    style={{ padding: "5px 10px", borderRadius: 8, border: `1.5px solid ${danger ? "#FFD3CA" : C.line}`, background: "#fff", color: danger ? C.coral : C.soft, fontWeight: 700, fontSize: 12.5, opacity: disabled ? .4 : 1 }}>{children}</button>;
}

function Leaderboard({ board, onRefresh }) {
  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>🏆 Ranking</div>
        <button className="btn" onClick={onRefresh} style={{ background: "none", border: `1.5px solid ${C.line}`, borderRadius: 999, padding: "4px 12px", fontSize: 12.5, fontWeight: 700, color: C.soft }}>↻ refresh</button>
      </div>
      {(!board || board.length === 0) ? (
        <div style={{ color: C.soft, fontSize: 14, padding: "16px 0" }}>No scores yet. Share the code and watch them roll in.</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {board.map((e, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderRadius: 12, background: i === 0 ? "#FFF8E5" : C.paper, border: `2px solid ${i === 0 ? C.gold : C.line}` }}>
              <span className="disp" style={{ fontWeight: 900, fontSize: 20, color: i === 0 ? C.gold : C.soft, width: 26 }}>{i + 1}</span>
              <span style={{ fontWeight: 700, flex: 1 }}>{e.name}{i === 0 ? " 👑" : ""}</span>
              <span style={{ fontWeight: 800, color: C.teal }}>{e.correct}<span style={{ color: C.soft, fontWeight: 600 }}>/{e.answered}</span></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Footer({ onCreate }) {
  const [t, setT] = useState("");
  return (
    <div style={{ marginTop: 56, paddingTop: 26, borderTop: `2px dashed ${C.line}` }}>
      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>⚡ Got a topic of your own?</div>
      <div style={{ color: C.soft, fontSize: 14, marginBottom: 12 }}>Spin up a fresh 60-card quiz on anything, edit it, get your own code.</div>
      <div style={{ display: "flex", gap: 10 }}>
        <input value={t} onChange={e => setT(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && t.trim()) { onCreate(t.trim()); setT(""); } }}
          placeholder="Type any topic…" style={{ flex: 1, fontSize: 16, padding: "14px 16px", borderRadius: 12, border: `2px solid ${C.line}`, background: "#fff" }} />
        <button onClick={() => { if (t.trim()) { onCreate(t.trim()); setT(""); } }} disabled={!t.trim()}
          style={{ padding: "0 22px", border: "none", borderRadius: 12, background: t.trim() ? C.coral : "#d8cdb6", color: "#fff", fontWeight: 700, fontSize: 16, cursor: t.trim() ? "pointer" : "default" }}>Generate →</button>
      </div>
    </div>
  );
}